import React from "react";
import toast from "react-hot-toast";
import useApi from "../useApi";

interface ISideEffectRes {
  msg: string;
  data: string[];
  status: string;
}

const useFetchSideEffectsPage = (billingCycleId: string) => {
  const [sideEffects, setSideEffects] = React.useState<string[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  const { makeApiCall } = useApi({
    url: `/rtm/patient-dashboard/cron/pdf/v3/side-effect/${billingCycleId}`,
    method: "GET",
    preAuth: true,
  });

  const fetchSideEffects = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { response, error: apiError } = await makeApiCall<
        undefined,
        ISideEffectRes
      >();

      if (apiError) {
        throw new Error(apiError.msg || "Failed to fetch side effects");
      }

      if (response?.status === "success") {
        setSideEffects(response.data || []);
        return response.data || [];
      } else {
        throw new Error(response?.msg || "No side effects data available");
      }
    } catch (error: any) {
      console.error("Error in fetchSideEffects:", error);
      setError(error.message);
      // toast.error(error.message || "Unable to load side effects. Please refresh and try again.");
      setSideEffects([]);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [billingCycleId, makeApiCall]);

  // Automatically fetch when the component mounts
  React.useEffect(() => {
    if (billingCycleId) {
      fetchSideEffects();
    }
  }, [fetchSideEffects, billingCycleId]);

  return {
    sideEffects,
    isLoading,
    error,
    refetch: fetchSideEffects,
  };
};

export default useFetchSideEffectsPage;
